import { Inject, Injectable, Optional, InjectionToken } from "@angular/core";
import { Observable, BehaviorSubject, of } from "rxjs";
import { catchError, tap, map } from "rxjs/operators";
import { UrlKey } from "../url/url.service";
import { IHttpService } from "../http/IHttpService";
import { WebLogger } from "../../web-logger/WebLogger";
import { GatekeeperFeature } from "./gatekeeper.model";

export interface GatekeeperConfig {
  featureIds: string;
  gatekeeperEnv: string;
}

export const GATEKEEPER_CONFIG = new InjectionToken<GatekeeperConfig>("GATEKEEPER_CONFIG");

@Injectable({
  providedIn: "root",
})
export class GatekeeperService {
  private features = new BehaviorSubject<GatekeeperFeature[]>([]);
  private initialized = false;

  constructor(
    @Inject("IHttpService") private httpService: IHttpService,
    @Optional() @Inject(GATEKEEPER_CONFIG) private config: GatekeeperConfig | null,
  ) {}

  initializeFeatures(): Observable<GatekeeperFeature[]> {
    if (this.initialized) {
      WebLogger.log({
        message: "Gatekeeper features already initialized, returning cached features",
        level: "debug",
        payload: {
          event_type: "gatekeeper_cache_hit",
          feature_count: this.features.value.length,
        },
      });
      return this.features.asObservable();
    }

    if (!this.config) {
      WebLogger.log({
        message: "Gatekeeper config not provided",
        level: "error",
        payload: {
          event_type: "gatekeeper_no_config",
        },
      });
      return of([]);
    }

    const urlParams = {
      featureIds: this.config.featureIds,
      env: this.config.gatekeeperEnv,
    };

    WebLogger.log({
      message: "Initializing gatekeeper features",
      level: "info",
      payload: {
        event_type: "gatekeeper_init_start",
        requested_features: this.config.featureIds,
        environment: this.config.gatekeeperEnv,
      },
    });

    return this.httpService
      .doSimpleGetRequest<GatekeeperFeature[]>(UrlKey.gatekeeper_url, urlParams)
      .pipe(
        map((response: unknown) => {
          if (
            Array.isArray(response) &&
            response.every(
              (item) => typeof item === "object" && "featureId" in item && "enabled" in item,
            )
          ) {
            return response as GatekeeperFeature[];
          }
          WebLogger.log({
            message: "Invalid gatekeeper feature response format",
            level: "error",
            payload: {
              event_type: "gatekeeper_invalid_response",
              response,
            },
          });
          throw new Error("Invalid feature response format");
        }),
        tap((features) => {
          this.features.next(features);
          this.initialized = true;

          const featureStates = features.reduce(
            (acc, feature) => {
              acc[feature.featureId] = {
                enabled: feature.enabled,
                lastToggledDate: feature.lastToggledDate,
              };
              return acc;
            },
            {} as Record<string, { enabled: boolean; lastToggledDate?: string }>,
          );

          WebLogger.log({
            message: "Gatekeeper features initialized successfully",
            level: "info",
            payload: {
              event_type: "gatekeeper_init_success",
              feature_count: features.length,
              feature_states: featureStates,
            },
          });
        }),
        catchError((error) => {
          WebLogger.log({
            message: "Gatekeeper features fetch error",
            level: "error",
            payload: {
              event_type: "gatekeeper_init_error",
              error: error?.message || error,
              requested_features: this.config?.featureIds,
              environment: this.config?.gatekeeperEnv,
            },
          });
          return of([]);
        }),
      );
  }

  getFeatures(): Observable<GatekeeperFeature[]> {
    return this.initialized ? this.features.asObservable() : this.initializeFeatures();
  }

  getFeatureById(featureId: string): Observable<GatekeeperFeature | undefined> {
    return this.getFeatures().pipe(
      map((features) => features.find((f) => f.featureId === featureId)),
    );
  }

  isFeatureEnabled(featureId: string): Observable<boolean> {
    return this.getFeatureById(featureId).pipe(
      map((feature) => {
        const enabled = feature?.enabled ?? false;
        WebLogger.log({
          message: `Checking ${featureId} feature state`,
          level: "debug",
          payload: {
            event_type: "gatekeeper_feature_check",
            feature_id: featureId,
            enabled,
            feature_present: !!feature,
            last_toggled: feature?.lastToggledDate,
          },
        });
        return enabled;
      }),
    );
  }
}
